import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';

import {
  blockCalculationType,
  deleteCalculationType,
  deleteTracker,
  editCalculationType,
  getCalculationTypes,
  getTrackers,
  saveCalculationType,
  saveTracker,
  unblockCalculationType,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import LockIcon from '../../../../packages/common/shared-ui/src/icons/LockIcon';
import PlusIcon from '../../../../packages/common/shared-ui/src/icons/PlusIcon';
import ReportDeleteIcon from '../../../../packages/common/shared-ui/src/icons/ReportDeleteIcon';
import SubmitIcon from '../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UnblockIcon from '../../../../packages/common/shared-ui/src/icons/UnblockIcon';
import UserNewEditIcon from '../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useDebouncedFunction } from '../../../../packages/common/utils/debounce';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: rowInput
const StyledRowInput = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: 0,
      '&::placeholder': {
        opacity: 1,
      },
    },
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '-5px',
    transform: 'none',
    left: '10px',
    top: '15px',
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '10px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
  },
}));
// Original class: rowButton
const StyledRowButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    borderLeft: '1px solid #E4E4EF',
    height: '100%',
    borderRadius: 0,
    minWidth: '32px',
  },

  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
    height: 'auto',
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7174AC',
      },
    },
  },
}));
// Original class: borderedItem
const StyledBorderedItem = styled(Grid)(theme => ({
  lineHeight: '20px',
  borderTop: '1px solid #E4E4EF',
}));
// Original class: settingsWrap
const StyledSettingsWrap = styled(Box)(theme => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFF',
  borderRadius: '16px',
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
const CalculationTypes = () => {
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('AdminCalculationTypes', 'title');
  const [types, setTypes] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [selectTrackers, setSelectTrackers] = useState([]);
  const [error, setError] = useState({ type: null, name: null, status: false });
  const Type = { CALCULATION: 'calculation', TRACKER: 'tracker' };

  const getTypes = useCallback(async () => {
    try {
      const { data: types } = await getCalculationTypes();
      setTypes(types);
    } catch (error) {
      console.error('ERROR WITH GET TYPES', error);
    }
  }, [setTypes]);

  const getObjectTrackers = useCallback(async () => {
    try {
      const { data: trackers } = await getTrackers();
      setTrackers(trackers.filter(item => item.isChecked));
      setSelectTrackers(trackers.filter(item => !item.isChecked));
    } catch (error) {
      console.error('ERROR WITH GET TRACKERS', error);
    }
  }, []);

  useEffect(() => {
    getTypes();
  }, [getTypes]);
  useEffect(() => {
    getObjectTrackers();
  }, [getObjectTrackers]);

  const handleTextField = useCallback(
    (event, index) => {
      const updatedObject = { ...types[index], name: event.target.value };
      setTypes([
        ...types.slice(0, index),
        updatedObject,
        ...types.slice(index + 1),
      ]);
    },
    [types],
  );
  const handleSelectField = useCallback(
    (event, index) => {
      const itemId = selectTrackers.find(
        item => item.valueName === event.target.value,
      );
      const updatedObject = {
        ...trackers[index],
        valueName: event.target.value,
        valueId: itemId.valueId,
      };
      setTrackers([
        ...trackers.slice(0, index),
        updatedObject,
        ...trackers.slice(index + 1),
      ]);
    },
    [selectTrackers, trackers],
  );
  const textField = useCallback(
    index => {
      return (
        <StyledRowInput
          autoFocus
          key={index}
          placeholder={getLocalString('labels', 'input')}
          variant='standard'
          defaultValue={types[index].name}
          onChange={event => handleTextField(event, index)}
        ></StyledRowInput>
      );
    },
    [handleTextField, getLocalString, types],
  );
  const selectField = useCallback(
    index => {
      return (
        <FormControl sx={{ width: '100%' }}>
          <StyledSelectLabel id='select-label'>
            {getLocalString('labels', 'select')}
          </StyledSelectLabel>
          <StyledRowSelect
            labelId='select-label'
            label={getLocalString('labels', 'select')}
            onChange={event => handleSelectField(event, index)}
            variant='standard'
            sx={{ width: '100%', border: 'none' }}
            value={trackers[index].valueName}
            MenuProps={{ slots: { paper: StyledPaperDropdown } }}
            IconComponent={props => (
              <ChevronIcon direction={'down'} {...props} />
            )}
          >
            {selectTrackers.map((item, index) => (
              <MenuItem key={index} value={item.valueName}>
                {item.valueName}
              </MenuItem>
            ))}
          </StyledRowSelect>
        </FormControl>
      );
    },
    [handleSelectField, getLocalString, selectTrackers, trackers],
  );
  const Error = ({ name }) => {
    const textString = `${getLocalString('AdminCalculationTypes', 'errors', 'errorPartOne')} ${name} ${getLocalString(
      'AdminCalculationTypes',
      'errors',
      'errorPartTwo',
    )}`;
    return (
      <Typography
        sx={{
          padding: '4px 0',
          lineHeight: '20px',
          fontSize: 16,
          fontWeight: 600,
          color: '#E03737',
        }}
      >
        {textString}
      </Typography>
    );
  };

  const typeMap = new Map([
    [
      Type.CALCULATION,
      {
        handler: saveCalculationType,
        deleteHandler: deleteCalculationType,
        input: textField,
        currentValue: types,
        getList: getTypes,
        setter: setTypes,
      },
    ],
    [
      Type.TRACKER,
      {
        handler: saveTracker,
        deleteHandler: deleteTracker,
        input: selectField,
        currentValue: trackers,
        getList: getObjectTrackers,
        setter: setTrackers,
      },
    ],
  ]);
  const saveEditHandler = useCallback(
    async (index, type) => {
      const itemId = types[index].id;
      const itemName = { name: types[index].name };
      const checkValue = types.filter(el => el.name === itemName.name);
      if (checkValue.length > 1) {
        setError({ type: type, name: itemName.name, status: true });
        return;
      }
      try {
        const response = await editCalculationType(itemId, itemName);
        if (response) {
          setError({ type: type, name: itemName.name, status: false });
          getTypes();
        }
      } catch (error) {
        console.error('ERROR WITH EDIT', error);
      }
    },
    [getTypes, types],
  );
  const saveHandler = useCallback(
    async (index, type) => {
      const item = typeMap.get(type).currentValue[index];
      const itemValue = item.name ? item.name : item.valueName;
      if (!itemValue) {
        return;
      }
      const checkValue = typeMap
        .get(type)
        .currentValue.filter(el =>
          el.name ? el.name === item.name : el.valueName === item.valueName,
        );
      if (checkValue.length > 1) {
        setError({ type: type, name: itemValue, status: true });
        return;
      }
      const valueToPost =
        type === Type.CALCULATION
          ? { name: types[index].name }
          : { trackerId: trackers[index].valueId };
      try {
        const response = await typeMap.get(type).handler(valueToPost);
        if (response) {
          setError({ type: type, name: itemValue, status: false });
          typeMap.get(type).getList();
        }
      } catch (error) {
        console.error('ERROR WITH SAVE', error);
      }
    },
    [Type.CALCULATION, trackers, typeMap, types],
  );

  const deleteHandler = useCallback(
    async (index, type) => {
      const id =
        type === Type.CALCULATION
          ? typeMap.get(type).currentValue[index].id
          : typeMap.get(type).currentValue[index].valueId;
      if (typeMap.get(type).currentValue[index].new) {
        const newArray = typeMap
          .get(type)
          .currentValue.filter((value, i) => i !== index);
        typeMap.get(type).setter(newArray);
        return;
      }
      try {
        const response = await typeMap.get(type).deleteHandler(id);
        if (response) {
          typeMap.get(type).getList();
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [Type.CALCULATION, typeMap],
  );

  const lockHandler = useCallback(
    async (index, type) => {
      const item = { id: types[index].id };
      try {
        const response = await blockCalculationType(item);
        if (response) {
          getTypes();
        }
      } catch (error) {
        console.error('ERROR WITH LOCK', error);
      }
    },
    [getTypes, types],
  );

  const unlockHandler = useCallback(
    async (index, type) => {
      const item = { id: types[index].id };
      try {
        const response = await unblockCalculationType(item);
        if (response) {
          getTypes();
        }
      } catch (error) {
        console.error('ERROR WITH UNLOCK', error);
      }
    },
    [getTypes, types],
  );

  const toggleEdit = useCallback(
    (index, type) => {
      const updatedObject = { ...types[index], edit: true };
      setTypes([
        ...types.slice(0, index),
        updatedObject,
        ...types.slice(index + 1),
      ]);
    },
    [types],
  );
  const addTypeHandler = useCallback(() => {
    setTypes([...types, { name: '', new: true }]);
  }, [types]);

  const addObjectHandler = useCallback(() => {
    setTrackers([...trackers, { valueName: '', new: true }]);
  }, [trackers]);

  const entityMap = new Map([
    [
      'edit',
      {
        handler: toggleEdit,
        icon: <UserNewEditIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'submit',
      {
        handler: saveHandler,
        icon: <SubmitIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'submitEdit',
      {
        handler: saveEditHandler,
        icon: <SubmitIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'delete',
      {
        handler: deleteHandler,
        icon: <ReportDeleteIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'block',
      {
        handler: lockHandler,
        icon: <LockIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'unblock',
      {
        handler: unlockHandler,
        icon: <UnblockIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
  ]);

  const RowButton = ({ children, action, rowIndex, type }) => {
    children = entityMap.get(action).icon;
    return (
      <StyledRowButton
        onClick={() => entityMap.get(action).handler(rowIndex, type)}
      >
        {children}
      </StyledRowButton>
    );
  };

  const TypeItemComponent = ({ item, rowIndex, type }) => {
    let buttons = ['delete'];
    type !== Type.TRACKER && (buttons = ['edit', 'delete', 'block']);
    item.new && (buttons = ['submit', 'delete']);
    item.edit &&
      type !== Type.TRACKER &&
      (buttons = ['submitEdit', 'delete', 'block']);
    !item.active &&
      type !== Type.TRACKER &&
      !item.new &&
      (buttons = ['edit', 'delete', 'unblock']);
    const value = item.name ? item.name : item.valueName;
    return (
      <StyledBorderedItem container>
        <Grid
          sx={{
            flex: 1,
            p: item.new && type === Type.TRACKER ? 0 : '10px',
            fontSize: '14px',
          }}
        >
          {item.edit || item.new ? typeMap.get(type).input(rowIndex) : value}
        </Grid>
        <Grid>
          {buttons.map((action, index) => (
            <RowButton
              key={index}
              action={action}
              rowIndex={rowIndex}
              type={type}
            ></RowButton>
          ))}
        </Grid>
      </StyledBorderedItem>
    );
  };
  return (
    <Page title={pageTitle} width='100%'>
      <Typography
        variant='h1'
        sx={{
          m: 0,
          padding: '10px 0',
          mt: '10px',
          mb: '30px',
        }}
      >
        {pageTitle}
      </Typography>
      <Grid container>
        <Grid
          size={{
            lg: 6,
          }}
        >
          {error.status && error.type === Type.CALCULATION && (
            <Error name={error.name}></Error>
          )}
        </Grid>
        <Grid
          size={{
            lg: 6,
          }}
        >
          {error.status && error.type === Type.TRACKER && (
            <Error name={error.name}></Error>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={5}>
        <Grid
          size={{
            lg: 6,
          }}
        >
          <StyledSettingsWrap>
            <Typography
              variant='h4'
              sx={{ padding: '16px 10px', fontWeight: 'bold' }}
            >
              {getLocalString(
                'AdminCalculationTypes',
                'labels',
                'calculationTypes',
              )}
            </Typography>
            {types.map((item, index) => (
              <TypeItemComponent
                key={index}
                item={item}
                rowIndex={index}
                type={Type.CALCULATION}
              ></TypeItemComponent>
            ))}
            <StyledBorderedItem>
              <IconButton onClick={addTypeHandler}>
                <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
              </IconButton>
            </StyledBorderedItem>
          </StyledSettingsWrap>
        </Grid>
        <Grid
          size={{
            lg: 6,
          }}
        >
          <StyledSettingsWrap>
            <Typography
              variant='h4'
              sx={{ padding: '16px 10px', fontWeight: 'bold' }}
            >
              {getLocalString('AdminCalculationTypes', 'labels', 'objects')}
            </Typography>
            {trackers.map((item, index) => (
              <TypeItemComponent
                key={index}
                item={item}
                rowIndex={index}
                type={Type.TRACKER}
              ></TypeItemComponent>
            ))}
            <StyledBorderedItem>
              <IconButton onClick={addObjectHandler}>
                <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
              </IconButton>
            </StyledBorderedItem>
          </StyledSettingsWrap>
        </Grid>
      </Grid>
    </Page>
  );
};
export default CalculationTypes;
